'use strict';

import '../../../css/fields/autocomplete.scss';
import { autocompleteElements } from "./elements/autocompleteElements";
import Base from "../Base";

export const EVENT_AUTOCOMPLETE_SELECT = 'event_autocomplete_select';
export const EVENT_AUTOCOMPLETE_SUBMIT_WITHOUT_SELECT = 'event_autocomplete_submit_without_select';

/**
 * @abstract
 */
export default class Autocomplete extends Base
{
    constructor(
        elements,
        xhr,
        select,
        transform = null,
        timed = 0,
        preventDefaultOnEnter = true
    ) {
        super();
        this.elements = { ...elements, ...autocompleteElements() };
        this.xhr = xhr;
        this.select = select;
        this.transform = transform;
        this.timed = timed;
        this.preventDefaultOnEnter = preventDefaultOnEnter;
        if (typeof this.elements.container === 'undefined' ||
            typeof this.elements.input === 'undefined'
        ) {
            throw Error('Define container and input in extending Autocomplete class');
        }
        this.callTimer = null;
        this.http = null;
        this.highlightIndex = -1;
        this.inputChange = this.onInputChange.bind(this);
        this.selectWithKeyboard = this.onSelectWithKeyboard.bind(this);
        this.overSuggestion = this.onOverSuggestion.bind(this);
        this.suggestionDown = this.onSuggestionDown.bind(this);
        this.noFocus = this.onNoFocus.bind(this);
        this.init();
    }

    addEventListeners() {
        let timeout = null;

        this.elements.input.addEventListener('keyup', (event) => {
            clearTimeout(timeout);
            timeout = setTimeout(this.inputChange, this.timed, event);
        });
        this.elements.input.addEventListener('keydown', this.selectWithKeyboard);
        this.elements.input.addEventListener('focusout', this.noFocus);
    }

    init() {
        this.suggestionContainer = this.elements.suggestionContainer();
        this.elements.container.append(this.suggestionContainer);
        this.addEventListeners();
    }

    clearSuggestions() {
        if (this.http !== null) {
            this.http.abort();
            this.http = null;
        }
        this.updateCurrentText();
        this.suggestionContainer.innerHTML = '';
        this.highlightIndex = -1;
    }

    showSuggestions(suggestions) {
        this.suggestions = [];
        this.suggestionContainer.innerHTML = '';
        const suggestionList = this.elements.suggestionList(
            suggestions,
            this.overSuggestion,
            this.suggestionDown
        );
        for (let suggestion of suggestions) {
            this.suggestions.push(suggestion);
        }
        if (this.suggestions.length > 0) {
            this.suggestionContainer.append(suggestionList);
        }
        if (typeof this.suggestions[0] !== 'undefined') {
            this.updateCurrentText();
        }
    }

    updateCurrentText() {
        if (typeof this.suggestions !== 'undefined' && typeof this.suggestions[0] !== 'undefined') {
            if (this.suggestions[0].title.indexOf(this.elements.input.value.toLowerCase()) > -1) {
                const currentText = this.elements.currentText(this.elements.input.value.toLowerCase());
                const listElement = this.elements.listElement(this.suggestions[0].id);
                if (listElement !== null) {
                    listElement.append(currentText);
                }
            }
        }
    }

    highlightSuggestion(index) {
        for (let suggestion of this.suggestions) {
            const listElement = this.elements.listElement(suggestion.id);
            listElement.classList.remove('highlight');
        }
        const highlightId = this.suggestions[index];
        const highlightListElement = this.elements.listElement(highlightId.id);
        highlightListElement.classList.add('highlight');
    }

    findIndexByObjectId(id, data) {
        for (let index in data) {
            if (data.hasOwnProperty(index)) {
                const check = data[index];
                if (check.id === id) {
                    return index;
                }
            }
        }
        return -1;
    }

    handleResponse(data) {
        this.http = data;
        let response = data.response;
        if (this.transform !== null) {
            response = this.transform(response);
        }
        this.showSuggestions(response);
    }

    onInputChange(event) {
        if (event.key === 'ArrowUp' ||
            event.key === 'ArrowDown' ||
            event.key === 'ArrowRight' ||
            event.key === 'Escape' ||
            event.key === 'Enter'
        ) {
            return;
        }
        const currentTarget = event.target;
        const query = currentTarget.value;
        if (query.length === 0) {
            this.clearSuggestions();
        }
        if (query.length > 1) {
            this.xhr(query)
                .then(data => {
                    this.handleResponse(data);
                })
                .catch(data => console.error(data));
        }

    }

    onSelectWithKeyboard(event) {
        switch (event.key) {
            case 'Enter':
                if (this.preventDefaultOnEnter) {
                    event.preventDefault();
                }
                if (this.highlightIndex > -1) {
                    // Always prevent default when selecting suggestion
                    event.preventDefault();
                    this.makeSelection();
                } else {
                    this.dispatchEvent(new CustomEvent(
                        EVENT_AUTOCOMPLETE_SUBMIT_WITHOUT_SELECT
                    ));
                }
                break;
            case 'ArrowDown':
                event.preventDefault();
                if (this.highlightIndex < this.suggestions.length-1) {
                    this.highlightIndex++;
                    this.highlightSuggestion(this.highlightIndex);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (this.highlightIndex > 0) {
                    this.highlightIndex--;
                    this.highlightSuggestion(this.highlightIndex);
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.elements.input.value = this.suggestions[0].title;
                this.clearSuggestions();
                break;
            case 'Escape':
                this.clearSuggestions();
                break;
            default:
                break;
        }
    }

    onOverSuggestion(event) {
        const currentTarget = event.currentTarget;
        const id = currentTarget.getAttribute('data-suggestion-id');
        this.highlightIndex = this.findIndexByObjectId(id, this.suggestions);
        this.highlightSuggestion(this.highlightIndex);
    }

    onSuggestionDown(event) {
        event.preventDefault();
        this.makeSelection();
    }

    onNoFocus() {
        this.clearSuggestions();
    }

    makeSelection() {
        const value = this.suggestions[this.highlightIndex];
        this.select(value);
        this.dispatchEvent(new CustomEvent(
            EVENT_AUTOCOMPLETE_SELECT,
            { detail: value }
        ));
        this.clearSuggestions();
    }
}
